import React, { useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookOutlinedIcon from "@mui/icons-material/Facebook";
import TelegramIcon from "@mui/icons-material/Telegram";
import InstagramIcon from "@mui/icons-material/Instagram";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";

export default function CommonShareDialog({
  open,
  onClose,
  title = "Share",
  name = "",
  description = "",
  image = "",
  price = null,
  location = "",
  shareUrl = "",
}) {
  /**
   * Detect mobile device
   */
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;

    const userAgent = window.navigator.userAgent || "";

    return /Android|iPhone|iPad|iPod/i.test(userAgent);
  }, []);

  /**
   * Detect native sharing support
   */
  const canNativeShare = useMemo(() => {
    if (typeof window === "undefined") return false;

    return typeof window.navigator.share === "function";
  }, []);

  /**
   * Detect HTTPS
   */
  const isSecureContextAvailable =
    typeof window !== "undefined" && window.isSecureContext;

  /**
   * Share URL fallback
   */
  const finalShareUrl =
    shareUrl ||
    (typeof window !== "undefined" ? window.location.href : "");

  /**
   * Format price
   */
  const formattedPrice =
    price !== null && price !== undefined && price !== ""
      ? `₹${Number(price).toLocaleString("en-IN")}`
      : "";

  /**
   * Share content
   */
  const shareText = [
    name ? `✨ ${name}` : "",
    "",
    "👗 Hindustan Wardrobe",
    formattedPrice
      ? `💰 Rental Price: ${formattedPrice} per day`
      : "",
    description ? `📝 ${description}` : "",
    location ? `📍 Location: ${location}` : "",
    "",
    "🛍️ Explore now:",
    finalShareUrl,
  ]
    .filter(Boolean)
    .join("\n");

  /**
   * Open external sharing URL
   */
  const openShareWindow = (url) => {
    if (!url) return;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  /**
   * WhatsApp
   */
  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(shareText);

    openShareWindow(`https://wa.me/?text=${text}`);
  };

  /**
   * Facebook
   */
  const shareOnFacebook = () => {
    const url = encodeURIComponent(finalShareUrl);

    openShareWindow(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`
    );
  };

  /**
   * Telegram
   */
  const shareOnTelegram = () => {
    const url = encodeURIComponent(finalShareUrl);
    const text = encodeURIComponent(
      `${name || "Hindustan Wardrobe"} - Hindustan Wardrobe`
    );

    openShareWindow(
      `https://t.me/share/url?url=${url}&text=${text}`
    );
  };

  /**
   * Copy text with fallback
   */
  const copyText = async (text) => {
    if (!text) return false;

    try {
      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      console.warn("Clipboard API failed:", error);
    }

    try {
      const textArea = document.createElement("textarea");

      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";

      document.body.appendChild(textArea);

      textArea.focus();
      textArea.select();

      const copied = document.execCommand("copy");

      document.body.removeChild(textArea);

      return copied;
    } catch (error) {
      console.error("Fallback copy failed:", error);
      return false;
    }
  };

  /**
   * Copy share content
   */
  const copyShareContent = async () => {
    return copyText(shareText);
  };

  /**
   * Instagram Story
   */
  const shareOnInstagramStory = async () => {
    const copied = await copyShareContent();

    if (copied) {
      alert(
        "Details copied successfully. Open Instagram and create your Story."
      );
    } else {
      alert(
        "Please copy the details manually and open Instagram Story."
      );
    }

    openShareWindow("https://www.instagram.com/");
  };

  /**
   * Instagram Feed
   */
  const shareOnInstagramFeed = async () => {
    const copied = await copyShareContent();

    if (copied) {
      alert(
        "Details copied successfully. Open Instagram and create your Feed post."
      );
    } else {
      alert(
        "Please copy the details manually and open Instagram Feed."
      );
    }

    openShareWindow("https://www.instagram.com/");
  };

  /**
   * Copy link
   */
  const copyLink = async () => {
    const copied = await copyText(finalShareUrl);

    if (copied) {
      alert("Link copied successfully!");
      onClose();
    } else {
      alert("Unable to copy link. Please copy it manually.");
    }
  };

  /**
   * Native Android/iOS share
   */
  const handleNativeShare = async () => {
    if (!canNativeShare) {
      alert(
        "Native sharing is not supported in this browser. Please open this website in Chrome or Safari."
      );
      return;
    }

    if (!isSecureContextAvailable) {
      alert(
        "Native sharing requires HTTPS. Please open the secure website URL."
      );
      return;
    }

    try {
      const shareData = {
        title: name || "Hindustan Wardrobe",
        text: shareText,
        url: finalShareUrl,
      };

      /**
       * Share image as a file when supported
       */
      if (
        image &&
        typeof navigator.canShare === "function" &&
        typeof File !== "undefined"
      ) {
        try {
          const response = await fetch(image);

          if (response.ok) {
            const blob = await response.blob();

            const extension =
              blob.type === "image/png" ? "png" : "jpg";

            const file = new File(
              [blob],
              `hindustan-wardrobe-${Date.now()}.${extension}`,
              {
                type: blob.type || "image/jpeg",
              }
            );

            const fileShareData = {
              ...shareData,
              files: [file],
            };

            if (navigator.canShare(fileShareData)) {
              await navigator.share(fileShareData);
              onClose();
              return;
            }
          }
        } catch (imageError) {
          console.warn(
            "Image sharing unavailable. Sharing text instead:",
            imageError
          );
        }
      }

      /**
       * Fallback: Share text and URL
       */
      await navigator.share(shareData);

      onClose();
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Native share failed:", error);

        alert(
          "Unable to open native sharing. Please try again or use Copy Link."
        );
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle
        sx={{
          fontFamily: "Georgia, serif",
          fontSize: 26,
          textAlign: "center",
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Preview */}
          <Box
            sx={{
              border: "1px solid #ddd7ce",
              background: "#f7f4ef",
              overflow: "hidden",
            }}
          >
            {image && (
              <Box
                component="img"
                src={image}
                alt={name || "Share preview"}
                sx={{
                  width: "100%",
                  height: 220,
                  objectFit: "cover",
                  display: "block",
                }}
              />
            )}

            <Box sx={{ p: 2 }}>
              <Typography
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: 20,
                }}
              >
                {name || "Hindustan Wardrobe"}
              </Typography>

              {formattedPrice && (
                <Typography
                  sx={{
                    fontSize: 13,
                    mt: 1,
                    color: "#77716a",
                  }}
                >
                  {formattedPrice} per day
                </Typography>
              )}

              {description && (
                <Typography
                  sx={{
                    fontSize: 12,
                    mt: 1,
                    color: "#67625c",
                    lineHeight: 1.6,
                  }}
                >
                  {description}
                </Typography>
              )}

              {location && (
                <Typography
                  sx={{
                    fontSize: 11,
                    mt: 1,
                    color: "#77716a",
                  }}
                >
                  📍 {location}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Native Android / iOS Share */}
          {isMobile && (
            <Button
              fullWidth
              startIcon={<ShareOutlinedIcon />}
              onClick={handleNativeShare}
              sx={buttonStyle}
            >
              Share using device
            </Button>
          )}

          {/* Mobile fallback */}
          {isMobile && !canNativeShare && (
            <>
              <Button
                fullWidth
                startIcon={<WhatsAppIcon />}
                onClick={shareOnWhatsApp}
                sx={buttonStyle}
              >
                Share on WhatsApp
              </Button>

              <Button
                fullWidth
                startIcon={<ContentCopyOutlinedIcon />}
                onClick={copyLink}
                sx={buttonStyle}
              >
                Copy Link
              </Button>
            </>
          )}

          {/* Desktop Sharing */}
          {!isMobile && (
            <>
              <Button
                fullWidth
                startIcon={<WhatsAppIcon />}
                onClick={shareOnWhatsApp}
                sx={buttonStyle}
              >
                Share on WhatsApp
              </Button>

              <Button
                fullWidth
                startIcon={<InstagramIcon />}
                onClick={shareOnInstagramStory}
                sx={buttonStyle}
              >
                Share on Instagram Story
              </Button>

              <Button
                fullWidth
                startIcon={<InstagramIcon />}
                onClick={shareOnInstagramFeed}
                sx={buttonStyle}
              >
                Share on Instagram Feed
              </Button>

              <Button
                fullWidth
                startIcon={<FacebookOutlinedIcon />}
                onClick={shareOnFacebook}
                sx={buttonStyle}
              >
                Share on Facebook
              </Button>

              <Button
                fullWidth
                startIcon={<TelegramIcon />}
                onClick={shareOnTelegram}
                sx={buttonStyle}
              >
                Share on Telegram
              </Button>

              <Button
                fullWidth
                startIcon={<ContentCopyOutlinedIcon />}
                onClick={copyLink}
                sx={buttonStyle}
              >
                Copy Link
              </Button>
            </>
          )}

        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            color: "#77716a",
            fontSize: 11,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const buttonStyle = {
  justifyContent: "flex-start",
  minHeight: 48,
  color: "#171717",
  border: "1px solid #ddd7ce",
  borderRadius: 0,
  px: 2,
};